import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import Badge from '@/components/ui/Badge';
import type { Checkpoint, CheckpointCostLevel } from '@/types/api';
import { TTS_CHECKPOINTS } from '@/types/api';

export interface CheckpointPreviewProps {
  currentStep?: string | null;
  pauseRequested?: boolean;
  completedSteps?: string[];
}

// Pipeline step order for determining completed steps
const STEP_ORDER = [
  'pdf_ingestion',
  'image_processing',
  'extract_structure',
  'synthesize_tts',
  'assemble_audio',
  'generate_credits',
  'qa_finalize',
];

function isStepCompleted(currentStep: string | null | undefined, targetStep: string, completedSteps?: string[]): boolean {
  // If we have explicit completed steps, use that
  if (completedSteps && completedSteps.includes(targetStep)) {
    return true;
  }

  if (!currentStep) return false;

  const currentIndex = STEP_ORDER.indexOf(currentStep);
  const targetIndex = STEP_ORDER.indexOf(targetStep);

  // If we can't find the steps, assume not completed
  if (currentIndex === -1 || targetIndex === -1) return false;

  // Target is completed if current step is past it
  return currentIndex > targetIndex;
}

function getCostBadgeVariant(costLevel: CheckpointCostLevel): 'danger' | 'warning' | 'slate' {
  switch (costLevel) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    default:
      return 'slate';
  }
}

export default function CheckpointPreview({
  currentStep,
  pauseRequested,
  completedSteps,
}: CheckpointPreviewProps) {
  // Filter to only show upcoming checkpoints
  const upcomingCheckpoints = TTS_CHECKPOINTS.filter(
    (checkpoint) => !isStepCompleted(currentStep, checkpoint.step, completedSteps)
  );

  // If no upcoming checkpoints, don't render
  if (upcomingCheckpoints.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
        <ClockIcon className="w-4 h-4" />
        Upcoming Pause Points
      </h4>
      <div className="space-y-2">
        {upcomingCheckpoints.map((checkpoint) => (
          <CheckpointItem
            key={checkpoint.id}
            checkpoint={checkpoint}
            pauseRequested={pauseRequested}
            isNext={checkpoint.step === getNextCheckpointStep(currentStep, upcomingCheckpoints)}
          />
        ))}
      </div>
      {pauseRequested && (
        <p className="mt-3 text-xs text-warning-600">
          Job will pause at the next checkpoint marked above.
        </p>
      )}
    </div>
  );
}

interface CheckpointItemProps {
  checkpoint: Checkpoint;
  pauseRequested?: boolean;
  isNext?: boolean;
}

function CheckpointItem({ checkpoint, pauseRequested, isNext }: CheckpointItemProps) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${
        isNext && pauseRequested
          ? 'bg-warning-50 border-warning-200'
          : 'bg-slate-50 border-slate-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isNext && pauseRequested
              ? 'bg-warning-100 text-warning-600'
              : 'bg-slate-200 text-slate-500'
          }`}
        >
          {isNext && pauseRequested ? (
            <CheckCircleIcon className="w-5 h-5" />
          ) : (
            <ClockIcon className="w-4 h-4" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-800">{checkpoint.label}</p>
          {checkpoint.description && (
            <p className="text-xs text-slate-500">{checkpoint.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={getCostBadgeVariant(checkpoint.cost_level)} size="sm">
          {checkpoint.cost_level.toUpperCase()} COST
        </Badge>
        {isNext && pauseRequested && (
          <Badge variant="warning" size="sm">
            Will Pause
          </Badge>
        )}
      </div>
    </div>
  );
}

function getNextCheckpointStep(
  currentStep: string | null | undefined,
  upcomingCheckpoints: Checkpoint[]
): string | null {
  if (!currentStep || upcomingCheckpoints.length === 0) {
    return upcomingCheckpoints[0]?.step || null;
  }

  const currentIndex = STEP_ORDER.indexOf(currentStep);
  if (currentIndex === -1) return upcomingCheckpoints[0]?.step || null;

  // Find the first upcoming checkpoint that comes after current step
  for (const checkpoint of upcomingCheckpoints) {
    const checkpointIndex = STEP_ORDER.indexOf(checkpoint.step);
    if (checkpointIndex > currentIndex) {
      return checkpoint.step;
    }
  }

  return upcomingCheckpoints[0]?.step || null;
}
